import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Grid,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  Paper,
  Stack,
  Typography,
  FormHelperText,
  IconButton
} from "@mui/material";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import VisibilityIcon from "@mui/icons-material/Visibility";
import LockResetIcon from "@mui/icons-material/LockReset";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Formik } from "formik";
import * as Yup from "yup";
import axios from "axios";
import { toast } from "react-toastify";
import CustomAlert from "../../component/AlertComponent/Alert";
import { baseURL } from "../../api";
import {
  normalizePasswordPolicyRegex,
  passwordMatchesPolicy,
  passwordPolicyPrompt
} from "../../utils/passwordPolicy";

// Helper to decode JWT and extract payload (without verifying signature)
function decodeJwt(token) {
  try {
    const base64Url = token.split(".")[1];
    if (!base64Url) return null;
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map(c => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    return JSON.parse(jsonPayload);
  } catch (e) {
    console.error("Failed to decode JWT:", e);
    return null;
  }
}

function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [username, setUsername] = useState("");
  const [refToken, setRefToken] = useState("");
  const [showPassword1, setShowPassword1] = useState(false);
  const [showPassword2, setShowPassword2] = useState(false);
  const [passwordPolicyRegex, setPasswordPolicyRegex] = useState(
    normalizePasswordPolicyRegex("")
  );
  const [passwordPolicyMessage, setPasswordPolicyMessage] = useState(
    passwordPolicyPrompt("")
  );

  useEffect(() => {
    const ref = searchParams.get("ref");
    if (!ref) {
      CustomAlert("error", "Invalid or missing reset link.", { position: "top-center" });
      navigate("/login");
      return;
    }

    setRefToken(ref);

    const payload = decodeJwt(ref);
    // Commonly, 'sub' holds the username or email in JWT
    const extractedUser = payload?.sub || payload?.email || "";
    if (!extractedUser) {
      CustomAlert("error", "Unable to read user information from reset link.", {
        position: "top-center"
      });
      navigate("/login");
      return;
    }

    setUsername(extractedUser);
    setPasswordPolicyRegex(
      normalizePasswordPolicyRegex(payload?.password_policy_regex)
    );
    setPasswordPolicyMessage(passwordPolicyPrompt(payload?.password_policy_msg));
  }, [searchParams, navigate]);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center"
      }}
    >
      <Paper sx={{ p: 4, maxWidth: 480, width: "100%" }}>
        <Typography variant="h5" sx={{ mb: 2 }}>
          Reset Password
        </Typography>

        <Formik
          enableReinitialize
          validateOnMount
          initialValues={{
            username: username,
            password: "",
            confirmPassword: ""
          }}
          validationSchema={Yup.object().shape({
            username: Yup.string().required("Username is required"),
            password: Yup.string()
              .test(
                "password-policy",
                passwordPolicyMessage,
                value => passwordMatchesPolicy(value, passwordPolicyRegex)
              )
              .required("Password is required"),
            confirmPassword: Yup.string()
              .oneOf([Yup.ref("password"), null], "Password cannot be confirmed")
              .required("Confirm Password is required")
          })}
          onSubmit={async (values, { setStatus, setSubmitting }) => {
            try {
              const payload = {
                username: values.username,
                password: values.password,
                confirmPassword: values.confirmPassword,
                passwordRef: refToken
              };

              await axios.post(`${baseURL}/user/forgot/password/reset`, payload, {
                headers: {
                  mode: "cors",
                  "Access-Control-Allow-Origin": "*",
                  "Access-Control-Allow-Methods": "GET,PUT,POST,DELETE,PATCH,OPTIONS",
                  "Content-Type": "application/json"
                }
              });

              toast.success("Password reset successfully. Please login with your new password.", {
                position: "top-center"
              });
              setStatus({ success: true });
              setSubmitting(false);
              navigate("/login");
            } catch (error) {
              const errorMessage =
                error?.response?.data?.metadata?.msg ||
                "Failed to reset password. Please try again.";
              CustomAlert("error", errorMessage, { position: "top-center" });
              setStatus({ success: false });
              setSubmitting(false);
            }
          }}
        >
          {({
            errors,
            handleBlur,
            handleChange,
            handleSubmit,
            isSubmitting,
            isValid,
            dirty,
            touched,
            values,
            setFieldValue
          }) => (
            <form noValidate onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Stack spacing={1}>
                    <InputLabel htmlFor="username-reset">User Name</InputLabel>
                    <OutlinedInput
                      id="username-reset"
                      type="text"
                      name="username"
                      value={values.username}
                      disabled
                      fullWidth
                    />
                    {touched.username && errors.username && (
                      <FormHelperText error id="helper-text-username-reset">
                        {errors.username}
                      </FormHelperText>
                    )}
                  </Stack>
                </Grid>

                <Grid item xs={12}>
                  <Stack spacing={1}>
                    <InputLabel htmlFor="password-reset">Password</InputLabel>
                    <OutlinedInput
                      id="password-reset"
                      type={showPassword1 ? "text" : "password"}
                      name="password"
                      value={values.password}
                      onBlur={handleBlur}
                      onChange={handleChange}
                      fullWidth
                      error={Boolean(touched.password && errors.password)}
                      endAdornment={
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="generate password"
                            onClick={() => {
                              const chars =
                                "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*_+|:.?";
                              let pwd = "";
                              const length = 16;
                              for (let attempt = 0; attempt < 100; attempt++) {
                                pwd = "";
                                for (let i = 0; i < length; i++) {
                                  const randomIndex = Math.floor(
                                    Math.random() * chars.length
                                  );
                                  pwd += chars[randomIndex];
                                }
                                if (passwordMatchesPolicy(pwd, passwordPolicyRegex)) break;
                              }
                              setFieldValue("password", pwd);
                              setFieldValue("confirmPassword", pwd);
                            }}
                            edge="end"
                            size="large"
                          >
                            <LockResetIcon />
                          </IconButton>
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={() => setShowPassword1(prev => !prev)}
                            edge="end"
                            size="large"
                          >
                            {showPassword1 ? <VisibilityIcon /> : <VisibilityOffIcon />}
                          </IconButton>
                        </InputAdornment>
                      }
                    />
                    {touched.password && errors.password && (
                      <FormHelperText error id="helper-text-password-reset">
                        {errors.password}
                      </FormHelperText>
                    )}
                    {!touched.password && (
                      <FormHelperText>
                        {passwordPolicyMessage}
                      </FormHelperText>
                    )}
                  </Stack>
                </Grid>

                <Grid item xs={12}>
                  <Stack spacing={1}>
                    <InputLabel htmlFor="confirm-password-reset">
                      Confirm Password
                    </InputLabel>
                    <OutlinedInput
                      id="confirm-password-reset"
                      type={showPassword2 ? "text" : "password"}
                      name="confirmPassword"
                      value={values.confirmPassword}
                      onBlur={handleBlur}
                      onChange={handleChange}
                      fullWidth
                      error={Boolean(
                        touched.confirmPassword && errors.confirmPassword
                      )}
                      endAdornment={
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle confirm password visibility"
                            onClick={() => setShowPassword2(prev => !prev)}
                            edge="end"
                            size="large"
                          >
                            {showPassword2 ? (
                              <VisibilityIcon />
                            ) : (
                              <VisibilityOffIcon />
                            )}
                          </IconButton>
                        </InputAdornment>
                      }
                    />
                    {touched.confirmPassword && errors.confirmPassword && (
                      <FormHelperText error id="helper-text-confirm-password-reset">
                        {errors.confirmPassword}
                      </FormHelperText>
                    )}
                  </Stack>
                </Grid>

                <Grid item xs={12}>
                  <Button
                    fullWidth
                    size="large"
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={isSubmitting || !dirty || !isValid}
                  >
                    Reset Password
                  </Button>
                </Grid>
              </Grid>
            </form>
          )}
        </Formik>
      </Paper>
    </Box>
  );
}

export default ResetPassword;
